import { z } from 'zod';

import { request } from '@/lib/api';

/* ---------------------------------------------------------------------------
   "התהליכים שלי" — חוזה ה-API (src/modules/workflows בשרת). הטיפוסים כאן הם
   מראה של workflow-dsl.ts; האימות האמיתי בשרת (Zod), בכל שמירה ובכל ריצה.
   --------------------------------------------------------------------------- */

export const TRIGGER_EVENTS = [
  'quote.sent',
  'quote.approved',
  'quote.declined',
  'quote.expired',
  'task.created',
  'task.assigned',
  'task.closed',
  'customer.created',
  'supplier_invoice.received',
  'invoice.issued',
] as const;
export type TriggerEvent = (typeof TRIGGER_EVENTS)[number];

export type Trigger =
  | { type: 'event'; event: TriggerEvent; delayDays?: number }
  | { type: 'daily'; hour: number }
  | { type: 'contract_notice'; daysBefore: number };

export type Condition =
  | { type: 'job_type'; op: 'in' | 'not_in'; jobTypeIds: string[] }
  | { type: 'amount'; op: 'gte' | 'lte'; value: number }
  | { type: 'customer_kind'; value: 'new' | 'returning' }
  | { type: 'task_priority'; priorities: number[] }
  | { type: 'assignee'; op: 'in' | 'none'; userIds: string[] }
  | { type: 'task_source'; sources: Array<'EMAIL' | 'MANUAL' | 'CUSTOMER_LINK' | 'QUOTE'> }
  | { type: 'task_field'; key: string; value: string }
  | { type: 'contract_category'; categories: string[] };

export const CUSTOMER_TEMPLATES = ['satisfaction', 'thank_you', 'quote_followup'] as const;
export type CustomerTemplate = (typeof CUSTOMER_TEMPLATES)[number];

export type Assign = { mode: 'none' } | { mode: 'auto' } | { mode: 'user'; userId: string };

export type Action =
  | {
      type: 'create_task';
      title: string;
      jobTypeId?: string;
      description?: string;
      dueInDays?: number;
      priority?: number;
      assign?: Assign;
    }
  | { type: 'add_checklist_items'; items: string[] }
  | {
      type: 'notify_team';
      message: string;
      includeOwners?: boolean;
      userIds?: string[];
      email?: boolean;
    }
  | { type: 'send_customer_message'; template: CustomerTemplate }
  | { type: 'reminder'; inDays: number; message: string }
  | { type: 'set_task_status'; status: 'IN_PROGRESS' }
  | { type: 'purchase_order_draft'; supplierId: string };

export interface WorkflowDefinition {
  name: string;
  description?: string;
  trigger: Trigger;
  conditions: Condition[];
  actions: Action[];
}

export const LIMITS = { maxWorkflows: 30, maxConditions: 10, maxActions: 5 } as const;

/** אילו ישויות יש בכל טריגר — קובע אילו תנאים/פעולות מוצעים בבונה (השרת אוכף). */
export type Subject =
  'task' | 'quote' | 'customer' | 'supplierInvoice' | 'contract' | 'salesInvoice';
export function triggerSubjects(t: Trigger): Subject[] {
  if (t.type === 'daily') return [];
  if (t.type === 'contract_notice') return ['contract'];
  if (t.event.startsWith('quote.')) return ['quote', 'customer'];
  if (t.event.startsWith('task.')) return ['task', 'customer'];
  if (t.event === 'customer.created') return ['customer'];
  if (t.event === 'invoice.issued') return ['salesInvoice', 'customer'];
  return ['supplierInvoice'];
}

export const CONDITION_NEEDS: Record<Condition['type'], Subject[]> = {
  job_type: ['task', 'quote'],
  amount: ['quote', 'supplierInvoice', 'salesInvoice'],
  customer_kind: ['customer'],
  task_priority: ['task'],
  assignee: ['task'],
  task_source: ['task'],
  task_field: ['task'],
  contract_category: ['contract'],
};
export const ACTION_NEEDS: Record<Action['type'], Subject[]> = {
  create_task: [],
  add_checklist_items: ['task'],
  notify_team: [],
  send_customer_message: ['customer'],
  reminder: [],
  set_task_status: ['task'],
  purchase_order_draft: ['quote'],
};

/** המשתנים שאפשר לכתוב בטקסט, לפי ישות. */
export const PLACEHOLDERS: Record<string, Subject[] | 'always'> = {
  'customer.name': ['customer'],
  'customer.phone': ['customer'],
  'task.title': ['task'],
  'jobType.name': ['task', 'quote'],
  'quote.number': ['quote'],
  'quote.total': ['quote'],
  'supplier.name': ['supplierInvoice', 'contract'],
  'invoice.number': ['supplierInvoice'],
  'invoice.amount': ['supplierInvoice'],
  'contract.title': ['contract'],
  'contract.endDate': ['contract'],
  'salesInvoice.number': ['salesInvoice'],
  'salesInvoice.total': ['salesInvoice'],
  'business.name': 'always',
  today: 'always',
};

export const available = (needs: Subject[] | 'always', subjects: Subject[]) =>
  needs === 'always' || needs.length === 0 || needs.some((s) => subjects.includes(s));

// ---------------------------------------------------------------------------

const runSchema = z.object({
  id: z.string(),
  workflowVersion: z.number(),
  eventName: z.string(),
  subjectType: z.string().nullable(),
  subjectId: z.string().nullable(),
  status: z.enum(['SCHEDULED', 'RUNNING', 'SUCCESS', 'SKIPPED', 'FAILED']),
  reason: z.string().nullable(),
  error: z.string().nullable(),
  actions: z.unknown(),
  scheduledFor: z.string().nullable(),
  finishedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type WorkflowRun = z.infer<typeof runSchema>;

export const workflowSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  enabled: z.boolean(),
  trigger: z.custom<Trigger>((v) => typeof v === 'object' && v !== null),
  conditions: z.custom<Condition[]>((v) => Array.isArray(v)),
  actions: z.custom<Action[]>((v) => Array.isArray(v)),
  version: z.number(),
  disabledReason: z.string().nullable(),
  lastRunAt: z.string().nullable(),
  recentRuns: z.array(runSchema).optional(),
});
export type Workflow = z.infer<typeof workflowSchema>;

const outcomeSchema = z.object({
  index: z.number(),
  type: z.string(),
  status: z.enum(['done', 'skipped', 'failed', 'planned']),
  detail: z.string().optional(),
  plan: z.record(z.unknown()).optional(),
});
export type ActionOutcome = z.infer<typeof outcomeSchema>;

export const testSchema = z.object({
  event: z.object({ id: z.string(), name: z.string(), at: z.string() }).nullable(),
  evaluation: z
    .object({
      status: z.enum(['SUCCESS', 'SKIPPED', 'FAILED']),
      reason: z.string().optional(),
      detail: z.string().optional(),
      conditions: z.array(
        z.object({
          index: z.number(),
          type: z.string(),
          passed: z.boolean(),
          detail: z.string().optional(),
        }),
      ),
      actions: z.array(outcomeSchema),
    })
    .nullable(),
});
export type TestResult = z.infer<typeof testSchema>;

export const WORKFLOWS_KEY = ['workflows'] as const;

export const workflowsApi = {
  list: (signal?: AbortSignal) =>
    request('/workflows', { schema: z.array(workflowSchema), signal }),
  runs: (id: string, signal?: AbortSignal) =>
    request(`/workflows/${id}/runs?take=100`, { schema: z.array(runSchema), signal }),
  create: (def: WorkflowDefinition & { enabled?: boolean }) =>
    request('/workflows', { method: 'POST', body: def, schema: workflowSchema }),
  update: (id: string, def: WorkflowDefinition) =>
    request(`/workflows/${id}`, { method: 'PUT', body: def, schema: workflowSchema }),
  setEnabled: (id: string, enabled: boolean) =>
    request(`/workflows/${id}/enabled`, {
      method: 'PUT',
      body: { enabled },
      schema: workflowSchema,
    }),
  remove: (id: string) => request(`/workflows/${id}`, { method: 'DELETE' }),
  test: (body: { definition?: WorkflowDefinition; workflowId?: string }) =>
    request('/workflows/test', { method: 'POST', body, schema: testSchema }),
};

/** שמות לתצוגה — מהרשימות של העסק. */
export interface Names {
  jobTypes: Record<string, string>;
  users: Record<string, string>;
  suppliers: Record<string, string>;
}

const listSchema = z.array(z.object({ id: z.string(), name: z.string() }).passthrough());
export const lookupsApi = {
  jobTypes: (signal?: AbortSignal) =>
    request('/job-type-templates', { schema: listSchema, signal }),
  team: (signal?: AbortSignal) =>
    request('/team', {
      schema: z.array(
        z.object({
          id: z.string(),
          name: z.string(),
          role: z.string().optional(),
          isActive: z.boolean().optional(),
        }),
      ),
      signal,
    }),
  suppliers: (signal?: AbortSignal) => request('/suppliers', { schema: listSchema, signal }),
};
